/**
 * Universal Config Adapter
 * - Server: Reads process.env
 * - Client: Reads window.__ENV (Bridge from layout.tsx)
 */

const getVar = (key) => {
  // First check this for client-side usage
  const targetWindow = typeof window !== 'undefined' ? window : (typeof global !== 'undefined' ? global.window : undefined);

  if (targetWindow && targetWindow.__ENV && targetWindow.__ENV[key] !== undefined) {
    return targetWindow.__ENV[key];
  }

  // Otherwise, use environment variables passed in via Kubernetes deployment (or set from .env.local for local dev)
  return process.env[key];
};

module.exports = {
  "api": getVar("API"),
  "app": getVar("APP"),
  "google_geocode": getVar("GOOGLE_GEOCODE"),
  "mapbox_key": getVar("MAPBOX_KEY"),
  "auth": {
    "domain": getVar("AUTH_DOMAIN"),
    "clientId": getVar("AUTH_CLIENT_ID"),
    "audience": getVar("AUTH_AUDIENCE")
  },
  "metabase": {
    "url": getVar("METABASE_URL")
  },
  "next-auth": {
    "NEXTAUTH_SECRET": getVar("NEXTAUTH_SECRET"),
    "NEXTAUTH_AUTH0_CLIENT_ID": getVar("NEXTAUTH_AUTH0_CLIENT_ID"),
    "NEXTAUTH_AUTH0_CLIENT_SECRET": getVar("NEXTAUTH_AUTH0_CLIENT_SECRET"),
    "NEXTAUTH_AUTH0_ISSUER": getVar("NEXTAUTH_AUTH0_ISSUER"),
    "NEXTAUTH_URL": getVar("NEXTAUTH_URL")
  },
  "eagle-eye": {
    "clientId": getVar("EAGLE_EYE_CLIENT_ID"),
    "redirectUrl": getVar("EAGLE_EYE_REDIRECT_URL")
  },
  "aipx-cloud": {
    "baseUrl": getVar("AIPX_CLOUD_BASE_URL")
  },
  "server-stats": {
    "url": getVar("SERVER_STATS_URL")
  },
  "azure-monitor": {
    "connectionString": getVar("AZURE_MONITOR_CONNECTION_STRING")
  },
  "video": {
    "baseUrl": getVar("VIDEO_BASE_URL"),
    "cloudVideo": {
      "basePath": getVar("VIDEO_CLOUD_VIDEO_BASE_PATH") || "/video"
    },
    "archiveUrl": getVar("VIDEO_ARCHIVE_URL")
  },
  "networkInfo": {
    "url": getVar("NETWORK_INFO_URL")
  },
  "ipconfigure": {
    "url": getVar("IPCONFIGURE_URL")
  },
  "hls": {
    "liveUrl": getVar("HLS_LIVE_URL")
  },
  "version": getVar("NEXT_PUBLIC_DD_VERSION"),
  "environment": getVar("NEXT_PUBLIC_ENV"),
  "uniview": {
    "experimentalWebCodecs": getVar("NEXT_PUBLIC_UNIVIEW_EXPERIMENTAL_WEBCODECS")
  },
  "s3": {
    "region": getVar("AWS_REGION") || getVar("AWS_DEFAULT_REGION"),
    "endpoint": getVar("AWS_ENDPOINT"),
    "forcePathStyle": getVar("AWS_FORCE_PATH_STYLE") === "true",
    "accessKeyId": getVar("AWS_ACCESS_KEY_ID"),
    "secretAccessKey": getVar("AWS_SECRET_ACCESS_KEY"),
    "sessionToken": getVar("AWS_SESSION_TOKEN")
  },
  // ---------------------------------------------------------------------------
  // Feature flags
  // ---------------------------------------------------------------------------
  // Boolean toggles for in-flight or experimental work. Default OFF — flip a
  // flag on per environment via the matching NEXT_PUBLIC_FEATURE_* env var
  // (`true` / `false`). Group by product area so it's obvious where to add
  // new flags and so callers can read `CONFIG.featureFlags.<area>.<flag>`.
  //
  // Precedence: every flag is ORed with its hard-coded twin in
  // `src/data/constants/integrationFeatureFlags.ts` at the call site
  // (`hardcoded || env`). That way:
  //   - Prod can ship a flag dark via the hard-coded constant and flip it on
  //     per-environment via the env var without a code edit.
  //   - Cypress / docker-compose can flip flags ON purely via env, so tests
  //     don't have to mutate source.
  "featureFlags": {
    "integrations": {
      // Gates the "Enhanced Integration" / Zoho path in the integration
      // wizard (the extra step between "Site Action" and "Choose Partner").
      // OFF by default: when false, the wizard falls back to the Standard
      // flow on every environment, regardless of hostname. Flip on by setting
      // NEXT_PUBLIC_FEATURE_INTEGRATIONS_ZOHO_ENHANCED=true.
      "zohoEnhancedIntegration": getVar("NEXT_PUBLIC_FEATURE_INTEGRATIONS_ZOHO_ENHANCED") === "true"
    },
    "player": {
      // Enables hls.js `debug: true` inside AvigilonAltaPlayer. Off in prod
      // because the debug logger floods the console (one of the documented
      // triggers for Chrome's "Page Unresponsive" dialog on Alta alerts).
      // Flip on locally with NEXT_PUBLIC_FEATURE_PLAYER_ALTA_HLS_DEBUG=true.
      "altaHlsDebug": getVar("NEXT_PUBLIC_FEATURE_PLAYER_ALTA_HLS_DEBUG") === "true"
    }
  }
};